"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOptionalCarouselAutoplayContext } from "./CarouselAutoplayContext.js";
const useCarouselAutoplayWithContext = (emblaApi, options)=>{
    const { autoplaySyncGlobally = false, carouselId, prefersReducedMotion } = options;
    const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false);
    const [autoplayIsManuallyPaused, setAutoplayIsManuallyPaused] = useState(prefersReducedMotion);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const isManuallyPausedRef = useRef(prefersReducedMotion);
    const hasUserInteractedRef = useRef(false);
    const context = useOptionalCarouselAutoplayContext();
    const registeredRef = useRef(false);
    if ('production' !== process.env.NODE_ENV) {
        if (autoplaySyncGlobally && !context) console.warn('[Carousel] syncGlobally is true but no CarouselAutoplayProvider found. Falling back to local autoplay.');
    }
    const resetAutoplayOnInteraction = useCallback((callback)=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return void callback();
        autoplay.reset();
        callback();
    }, [
        emblaApi
    ]);
    const pauseCarousel = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) autoplay.stop();
    }, [
        emblaApi
    ]);
    const resumeCarousel = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) autoplay.play();
    }, [
        emblaApi
    ]);
    const isCarouselPlaying = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        return autoplay ? autoplay.isPlaying() : false;
    }, [
        emblaApi
    ]);
    const handleGlobalAutoplayPause = useCallback(()=>{
        if (autoplaySyncGlobally && context) context.pauseAllCarousels();
    }, [
        autoplaySyncGlobally,
        context
    ]);
    const handleGlobalAutoplayResume = useCallback(()=>{
        if (autoplaySyncGlobally && context) context.resumeAllCarousels();
    }, [
        autoplaySyncGlobally,
        context
    ]);
    const toggleAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        hasUserInteractedRef.current = true;
        setHasUserInteracted(true);
        if (prefersReducedMotion) {
            const newPausedState = !autoplayIsManuallyPaused;
            isManuallyPausedRef.current = newPausedState;
            setAutoplayIsManuallyPaused(newPausedState);
            if (newPausedState) {
                autoplay.stop();
                handleGlobalAutoplayPause();
            } else {
                autoplay.reset();
                autoplay.play();
                handleGlobalAutoplayResume();
            }
        } else {
            const wasPlaying = autoplay.isPlaying();
            if (wasPlaying) {
                autoplay.stop();
                isManuallyPausedRef.current = true;
                setAutoplayIsManuallyPaused(true);
                handleGlobalAutoplayPause();
            } else {
                isManuallyPausedRef.current = false;
                setAutoplayIsManuallyPaused(false);
                autoplay.reset();
                autoplay.play();
                handleGlobalAutoplayResume();
            }
        }
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused,
        handleGlobalAutoplayPause,
        handleGlobalAutoplayResume
    ]);
    const handleToggleClick = useCallback((event)=>{
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        toggleAutoplay();
    }, [
        toggleAutoplay
    ]);
    const manuallyPauseAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) {
            if (autoplay.isPlaying()) autoplay.stop();
            isManuallyPausedRef.current = true;
            setAutoplayIsManuallyPaused(true);
            handleGlobalAutoplayPause();
        }
    }, [
        emblaApi,
        handleGlobalAutoplayPause
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!autoplaySyncGlobally || !context || !emblaApi || registeredRef.current) return;
        const controls = {
            emblaApi,
            pause: pauseCarousel,
            resume: resumeCarousel,
            isPlaying: isCarouselPlaying
        };
        context.registerCarousel(carouselId, controls);
        registeredRef.current = true;
        return ()=>{
            context.unregisterCarousel(carouselId);
            registeredRef.current = false;
        };
    }, [
        autoplaySyncGlobally,
        context,
        emblaApi,
        carouselId,
        pauseCarousel,
        resumeCarousel,
        isCarouselPlaying
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        const updateState = ()=>{
            const isPlaying = autoplay.isPlaying();
            setAutoplayIsPlaying(isPlaying);
        };
        updateState();
        const onPlay = ()=>{
            setAutoplayIsPlaying(true);
        };
        const onStop = ()=>{
            setAutoplayIsPlaying(false);
        };
        emblaApi.on('autoplay:play', onPlay).on('autoplay:stop', onStop).on('reInit', updateState);
        return ()=>{
            emblaApi.off('autoplay:play', onPlay).off('autoplay:stop', onStop);
        };
    }, [
        emblaApi
    ]);
    useEffect(()=>{
        if (!autoplaySyncGlobally || !context) return;
        if (context.globalAutoplayPaused && !autoplayIsManuallyPaused) {
            isManuallyPausedRef.current = true;
            setAutoplayIsManuallyPaused(true);
        }
    }, [
        autoplaySyncGlobally,
        context,
        context?.globalAutoplayPaused,
        autoplayIsManuallyPaused
    ]);
    const effectiveAutoplayIsPlaying = prefersReducedMotion ? !autoplayIsManuallyPaused : autoplayIsPlaying;
    return {
        autoplayIsPlaying: effectiveAutoplayIsPlaying,
        autoplayIsManuallyPaused,
        hasUserInteracted,
        toggleAutoplay: handleToggleClick,
        resetAutoplayOnInteraction,
        manuallyPauseAutoplay,
        isManuallyPausedRef,
        hasUserInteractedRef,
        globalAutoplayPaused: context?.globalAutoplayPaused,
        globalAutoplayPausedRef: context?.globalAutoplayPausedRef
    };
};
export { useCarouselAutoplayWithContext };
